import {
  createContext,
  useContext,
  useState,
  useCallback,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
} from "react";

/* ── Context ─────────────────────────────────────────────────────────────── */

interface SidebarContextValue {
  openGroups: Set<string>;
  setOpenGroups: Dispatch<SetStateAction<Set<string>>>;
  toggleGroup: (id: string) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

/* ── Provider ────────────────────────────────────────────────────────────── */

/**
 * Wrap the layout that owns a collapsible sidebar section with this provider.
 * The open/closed state of groups lives here, outside SidebarNavItems, so it
 * survives re-mounts caused by route changes within the same section.
 *
 * Example usage (in your analytics layout or NavSidebarDouble):
 *
 *   <SidebarProvider>
 *     <NavSidebarDouble />
 *     <Outlet />
 *   </SidebarProvider>
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <SidebarContext.Provider value={{ openGroups, setOpenGroups, toggleGroup }}>
      {children}
    </SidebarContext.Provider>
  );
}

/* ── Hook ────────────────────────────────────────────────────────────────── */

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used inside <SidebarProvider>");
  }
  return ctx;
}
